import {
  InnspelApiError,
  InnspelQueueFullError,
  type AttachmentInput,
  type InnspelClient,
  type KnownIssueCard,
  type MySubmission,
  type OpenFeedbackOptions,
  type PatternFinding,
} from '@innspel/core';
import { useCallback, useEffect, useRef, useState } from 'react';
import { pagePath } from '../kontekst.ts';
import { useOffline } from '../nett.ts';
import { readImageFile } from '../skjermbilde/fil.ts';
import { compose, type Masking } from '../skjermbilde/komposisjon.ts';
import type { Fangst, ScreenshotAdapter } from '../skjermbilde/typer.ts';
import { Dialog, useForm } from './Dialog.tsx';
import { Felt } from './Felt.tsx';
import { KjenteFeil } from './KjenteFeil.tsx';
import { Kvittering, type KvitteringArt } from './Kvittering.tsx';
import { erOffline, hentMine, oppdaterSistKjent, type SistKjent } from './sistKjent.ts';
import { MySubmissions } from './MineInnspill.tsx';
import { Banner, Btn, Hint } from './primitiver.tsx';
import { Skjermbilde } from './Skjermbilde.tsx';
import { saksnummerAv } from './saksnummer.ts';
import { tekst } from './tekster.ts';
import { Valg } from './Valg.tsx';
import type { MaskRect, SubmissionStatusView } from '@innspel/core';

export type Step = 'valg' | 'kjenteFeil' | 'felt' | 'skjermbilde' | 'kvittering' | 'mine';

/** What the submitter has written — what must survive a new login (tech-decisions.md, «Portalen»). */
export interface Draft {
  track: 'feil' | 'onske';
  text: string;
}

/**
 * What the host gets to know when a submission has been sent (F26). The case number, never the
 * submitter's identity — the number is what they already see in the receipt. `queued` when the
 * network was gone and the submission is in the queue: they tapped Send, and that counts.
 * «Ja, jeg også» on a known issue does not give this call — that is Reach, not a new case.
 * Same shape as in react-native.
 */
export interface SubmittedInfo {
  type: 'feil' | 'onske';
  queued: boolean;
  /** Present when the case has been created on the server; missing in the queue. */
  id?: string;
  /** The case number the submitter sees in the receipt («Sak 10042»). Follows `id`: missing in the queue. */
  number?: number;
}

export interface OpenOptions extends OpenFeedbackOptions {
  /** A restored draft, typically after «Logg inn på nytt» (log in again). */
  draft?: Draft;
  /**
   * Open straight into «Mine innspill» (my submissions, DD-62). Also in `OpenFeedbackOptions`
   * from `@innspel/core` 0.9.0; repeated here so the package builds against 0.8.0 on npm.
   */
  view?: 'mine';
}

export interface WidgetProps {
  client: InnspelClient;
  open: boolean;
  onClose: () => void;
  apneOpsjoner?: OpenOptions;
  /** The tenant's name — on the reply in «Mine innspill» and in «Logg inn på nytt hos …». */
  tenantName?: string;
  /** Capture of the page. `null` = «Ta bilde av siden» does not exist; «Last opp bilde» still exists when the tenant has screenshots on. */
  screenshot: ScreenshotAdapter | null;
  /** The host's sentence at the image step, shared or per track. */
  imageStepNote?: string | { feil?: string; onske?: string };
  colorScheme?: 'dark' | 'light';
  returnFocusTo?: HTMLElement | null;
  /**
   * The token expired while the submitter was writing. The host gets the draft and takes the
   * submitter to a new login; the text is not lost. Without this only the error is shown.
   */
  onIdentityExpired?: (utkast: Draft) => void;
  /**
   * «Send anonymt» (send anonymously) after an expired token: the host makes the client anonymous
   * (the portal: logs out), and the widget sends again when `client.isAnonymous` is true.
   */
  onSendAnonymously?: () => void;
  /** F26: called after a submission — sent or queued. Never for «ja, jeg også». */
  onSubmitted?: (info: SubmittedInfo) => void;
}

/**
 * The widget — the same flow as `@innspel/react-native`, in a dialog (DD-47).
 *
 * The bug track meets known issues BEFORE the field. The wish track goes straight from the field
 * to sending: the contract has no wish cards, and the step «Har noen ønsket dette?» comes
 * back when it does (DD-03, clarified 2026-09-14 — until then Send went in circles
 * without a match). The screenshot is created only on tap, never on opening (K4),
 * and exists only when the tenant has screenshots on (DD-26). The text is sent
 * first; the image can fail on its own (DD-42). The queue is flushed when the dialog OPENS, never
 * at start-up (DD-41).
 *
 * **The text is never lost** (UX review C1). Three ways out of the field threw away
 * 120 characters before 2026-09-14: «Tilbake» (back), Esc and a stray click on the scrim. Now
 * «Tilbake» goes to the previous STEP without clearing, closing keeps the draft until the dialog
 * opens again, and the scrim does not close at all when there is something in the field.
 * The receipt is the one place that resets — there the submission has been sent.
 *
 * **The primary button sits in the dialog's footer** (C2), outside what scrolls, and
 * the masking state is lifted here because «Legg ved» (attach) is that button on the
 * image step.
 *
 * Web-specific: `screen` is the URL path without query and hash when the host does not give
 * a route name; the receipt says «Sendt anonymt» when the client is anonymous; and an
 * expired token while writing keeps the draft.
 */
export function Widget({ client, open, onClose, apneOpsjoner, tenantName, screenshot, imageStepNote, colorScheme, returnFocusTo, onIdentityExpired, onSendAnonymously, onSubmitted }: WidgetProps) {
  const [steg, setSteg] = useState<Step>('valg');
  const [spor, setSpor] = useState<'feil' | 'onske'>('feil');
  const bildeNotat = typeof imageStepNote === 'string' ? imageStepNote : imageStepNote?.[spor];
  const [verdi, setVerdi] = useState('');
  const [kort, setKort] = useState<KnownIssueCard[]>([]);
  const [lasterKort, setLasterKort] = useState(false);
  const [lenketTil, setLenketTil] = useState<KnownIssueCard | undefined>();
  const [funn, setFunn] = useState<PatternFinding[]>([]);
  const [fjernede, setFjernede] = useState<string[]>([]);
  const [sender, setSender] = useState(false);
  const [feil, setFeil] = useState<string | undefined>();
  const [utlopt, setUtlopt] = useState(false);
  const [sendNårAnonym, setSendNårAnonym] = useState(false);
  const [kvittering, setKvittering] = useState<{ art: KvitteringArt; nr?: number; vedlegg?: 'feilet' | 'forkastet'; frist?: string; sak?: SubmissionStatusView }>();
  // F32 på web (DD-69 presisert): dialogens fot bærer «Send nytt innspill» — den er borte
  // mens et tillegg skrives, så første trykk aldri treffer feil knapp.
  const [tilleggApent, setTilleggApent] = useState(false);
  const [mine, setMine] = useState<MySubmission[]>([]);
  const [lasterMine, setLasterMine] = useState(false);
  // H3.1 (konseptsidens skjerm 3): satt når `mine` er lista fra forrige henting, vist uten nett.
  const [mineSistKjentAt, setMineSistKjentAt] = useState<string | undefined>();
  const sistHentet = useRef<SistKjent | undefined>(undefined);
  const [fangst, setFangst] = useState<Fangst | undefined>();
  const [komponerer, setKomponerer] = useState(false);
  const [vedlegg, setVedlegg] = useState<AttachmentInput | undefined>();
  /** The number of fields that were covered when the image was attached — the chip only counts when there is something to count (C9). */
  const [vedleggFelt, setVedleggFelt] = useState(0);
  // Maskeringen bor her, ikke i Skjermbilde: «Legg ved» står i dialogens fot (C2).
  const [egneMasker, setEgneMasker] = useState<MaskRect[]>([]);
  const [allTekst, setAllTekst] = useState(false);
  const åpnetMed = useRef<OpenOptions | undefined>(undefined);
  const form = useForm();
  const offline = useOffline();

  const skjerm = apneOpsjoner?.screen ?? (typeof location === 'undefined' ? undefined : pagePath(location.href));
  // Leietakerens grenser: startverdiene fra init(), oppdatert fra plattformen ved åpning (M2.5).
  const [innstillinger, setInnstillinger] = useState({ ...client.settings });
  const skjermbildePå = innstillinger.screenshotsEnabled;

  const nullstill = useCallback(() => {
    setSteg('valg');
    setSpor('feil');
    setVerdi('');
    setKort([]);
    setLenketTil(undefined);
    setFunn([]);
    setFjernede([]);
    setFeil(undefined);
    setUtlopt(false);
    setSendNårAnonym(false);
    setKvittering(undefined);
    setFangst(undefined);
    setVedlegg(undefined);
    setVedleggFelt(0);
    setEgneMasker([]);
    setAllTekst(false);
    setKomponerer(false);
  }, []);

  // «Mine innspill» åpnet av verten (`view: 'mine'`) er en visning OVER det innsenderen
  // holdt på, ikke et steg i skjemaet (F3). `stegForMine` er steget under — det lukking
  // og «Tilbake» fører tilbake til, så et utkast i feltet står der ved neste åpning.
  const [mineDirekte, setMineDirekte] = useState(false);
  const [stegForMine, setStegForMine] = useState<Step>('valg');
  const stegRef = useRef(steg);
  stegRef.current = steg;

  /** Something the submitter has made and not sent. It wins over the host's `track` (F1). */
  const harSkrevet = verdi.trim().length > 0 || vedlegg !== undefined;

  /**
   * Closing keeps the draft (C1). What has been written stays until the dialog opens
   * again — an Esc or a stray click is not a decision to discard anything.
   */
  const lukk = useCallback(() => {
    onClose();
    if (stegRef.current === 'mine' && mineDirekte) {
      setSteg(stegForMine);
      setMineDirekte(false);
    }
  }, [onClose, mineDirekte, stegForMine]);

  /** The receipt is done: reset, otherwise the next submission opens in the old reply. */
  const lukkOgNullstill = useCallback(() => {
    onClose();
    nullstill();
    setMineDirekte(false);
    setStegForMine('valg');
  }, [onClose, nullstill]);

  const hentKort = useCallback(async () => {
    setLasterKort(true);
    try {
      setKort(await client.knownIssues.for(skjerm ? { screen: skjerm } : {}));
    } finally {
      setLasterKort(false);
    }
  }, [client, skjerm]);

  const velgSpor = useCallback(
    async (valgt: 'feil' | 'onske') => {
      setSpor(valgt);
      client.track({ name: 'track_selected', type: valgt });
      if (valgt === 'onske') {
        setSteg('felt');
        return;
      }
      setSteg('kjenteFeil');
      await hentKort();
    },
    [client, hentKort],
  );

  // Køen tømmes når dialogen ÅPNES — aldri ved oppstart (DD-41). Et gjenopprettet
  // utkast (etter ny innlogging) legges rett i feltet.
  useEffect(() => {
    if (!open) return;
    åpnetMed.current = apneOpsjoner;
    client.track({ name: 'widget_opened' });
    void client.queue.flush().catch(() => undefined);
    // Konfigurasjonen er et tilbud: svarer den, oppdateres grensene; ellers står de.
    void client.loadSettings().then((s) => setInnstillinger({ ...s })).catch(() => undefined);
    // «Mine innspill» (DD-62): tellingen på knappen hentes ved åpning, stille — feiler
    // kallet, står knappen uten tall. Anonym modus har ingen liste og kalles aldri.
    if (!client.isAnonymous) {
      void hentMine(client, sistHentet, erOffline())
        .then((r) => {
          setMine(r.rader);
          setMineSistKjentAt(r.sistKjentAt);
        })
        .catch(() => undefined);
    }
    if (apneOpsjoner?.draft) {
      setSpor(apneOpsjoner.draft.track);
      setVerdi(apneOpsjoner.draft.text);
      setFunn(client.scanText(apneOpsjoner.draft.text));
      setSteg('felt');
    } else if (apneOpsjoner?.view === 'mine' && !client.isAnonymous) {
      // Lista legges over et eventuelt utkast og tar det ikke bort (F1, F3).
      void visMine(true);
    } else if (apneOpsjoner?.track && !harSkrevet) {
      // Et utkast vinner over vertens spor: å bytte spor under en skrevet tekst ville
      // sendt en feil som ønske. Innsenderen står der hun var.
      void velgSpor(apneOpsjoner.track);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Treffene er et tilbud, aldri en sperre: tom liste hopper skjermen over.
  useEffect(() => {
    if (steg === 'kjenteFeil' && !lasterKort && kort.length === 0) setSteg('felt');
  }, [steg, lasterKort, kort.length]);

  const endreTekst = useCallback(
    (v: string) => {
      setVerdi(v);
      setFunn(client.scanText(v));
    },
    [client],
  );

  const taBilde = useCallback(async () => {
    if (!screenshot) return;
    setFeil(undefined);
    try {
      setEgneMasker([]);
      setAllTekst(false);
      setFangst(await screenshot.capture(document.body));
      setSteg('skjermbilde');
    } catch {
      setFeil(tekst.skjermbildeFeilet);
    }
  }, [screenshot]);

  const lastOpp = useCallback(async (fil: File) => {
    setFeil(undefined);
    try {
      setEgneMasker([]);
      setAllTekst(false);
      setFangst(await readImageFile(fil));
      setSteg('skjermbilde');
    } catch {
      setFeil(tekst.filFeilet);
    }
  }, []);

  const leggVed = useCallback(
    async (m: Masking) => {
      if (!fangst) return;
      setKomponerer(true);
      try {
        setVedlegg(await compose(fangst, m));
        setVedleggFelt(m.automasker.length + m.egne.length);
      } catch {
        setFeil(tekst.skjermbildeFeilet);
      } finally {
        setKomponerer(false);
        setFangst(undefined);
        setSteg('felt');
      }
    },
    [fangst],
  );

  const fjernBilde = useCallback(() => {
    setFangst(undefined);
    setVedlegg(undefined);
    setVedleggFelt(0);
    setEgneMasker([]);
    setAllTekst(false);
    setSteg('felt');
  }, []);

  const send = useCallback(async () => {
    setSender(true);
    setFeil(undefined);
    setUtlopt(false);
    try {
      const res = await client.capture({
        type: spor,
        text: verdi,
        ...(vedlegg ? { attachment: vedlegg } : {}),
        ...(spor === 'feil' && lenketTil ? { relatedKnownIssueId: lenketTil.id } : {}),
        context: fjernetKontekst(client, fjernede, skjerm),
      } as never);

      if (res.status === 'queued') {
        setKvittering({ art: 'kø', ...(res.attachment ? { vedlegg: 'forkastet' as const } : {}) });
      } else {
        const nr = saksnummerAv(res.submission);
        setKvittering({
          art: client.isAnonymous ? 'anonym' : spor,
          ...(nr !== undefined ? { nr } : {}),
          // Saken slik serveren svarte: kvitteringskortet henter sporingslinjens ledd derfra.
          sak: res.submission,
          // Tidsløftet i kvitteringen er DATOEN fra svaret, aldri «innen 90 dager» (DD-07, DD-28).
          ...(res.submission.deadlineAt ? { frist: res.submission.deadlineAt } : {}),
          ...(res.attachment?.status === 'failed' ? { vedlegg: 'feilet' as const } : {}),
        });
      }
      setSteg('kvittering');
      // F26: verten får vite at det skjedde — etter kvitteringen, så et kast i vertens
      // kode aldri kan holde innsenderen fra å se at innspillet gikk.
      if (res.status === 'queued') {
        onSubmitted?.({ type: spor, queued: true });
      } else {
        const number = saksnummerAv(res.submission);
        onSubmitted?.({ type: spor, queued: false, id: res.submission.id, ...(number !== undefined ? { number } : {}) });
      }
    } catch (e) {
      // Teksten mistes ALDRI: den står i feltet, og årsaken er i klartekst.
      if (e instanceof InnspelApiError && e.code === 'feedback_token_expired') {
        setUtlopt(true);
      } else if (e instanceof InnspelQueueFullError) setFeil(e.userMessage);
      else if (e instanceof InnspelApiError) setFeil(e.message);
      else setFeil(tekst.tjenerenSvarteIkke);
    } finally {
      setSender(false);
    }
  }, [client, spor, verdi, vedlegg, lenketTil, fjernede, skjerm, onSubmitted]);

  // «Send anonymt» etter utløpt token: verten har gjort klienten anonym; da sendes det.
  useEffect(() => {
    if (sendNårAnonym && client.isAnonymous) {
      setSendNårAnonym(false);
      void send();
    }
  }, [sendNårAnonym, client.isAnonymous, send]);

  const jaOgsa = useCallback(
    async (k: KnownIssueCard) => {
      setSender(true);
      try {
        const res = await client.knownIssues.confirm(k.id);
        const nr = saksnummerAv(res);
        setKvittering({ art: 'jaOgsa', ...(nr !== undefined ? { nr } : {}) });
        setSteg('kvittering');
      } catch (e) {
        if (e instanceof InnspelApiError && e.code === 'feedback_token_expired') setUtlopt(true);
        else setFeil(e instanceof InnspelApiError ? e.message : tekst.tjenerenSvarteIkke);
      } finally {
        setSender(false);
      }
    },
    [client],
  );

  /** `direkte`: the host opened the list (`view: 'mine'`). Otherwise the submitter came via the button on the choice step. */
  const visMine = useCallback(async (direkte = false) => {
    if (direkte) {
      if (stegRef.current !== 'mine') setStegForMine(stegRef.current);
    } else {
      setStegForMine('valg');
    }
    setMineDirekte(direkte);
    setSteg('mine');
    setLasterMine(true);
    try {
      const r = await hentMine(client, sistHentet, erOffline());
      setMine(r.rader);
      setMineSistKjentAt(r.sistKjentAt);
    } catch (e) {
      setMine([]);
      setMineSistKjentAt(undefined);
      if (e instanceof InnspelApiError && e.code === 'feedback_token_expired') setUtlopt(true);
    } finally {
      setLasterMine(false);
    }
  }, [client]);

  /**
   * One step back, NEVER a reset (C1). From the field the previous step is
   * known issues in the bug track when there were matches — otherwise the choice step.
   */
  const gåTilbake = useCallback(() => {
    setFeil(undefined);
    // F3: åpnet verten lista direkte, finnes det ikke noe forrige steg i widgeten.
    if (steg === 'mine') {
      if (mineDirekte) lukk();
      else setSteg('valg');
      return;
    }
    if (steg === 'skjermbilde') {
      setFangst(undefined);
      setSteg('felt');
      return;
    }
    if (steg === 'felt' && spor === 'feil' && kort.length > 0) {
      setSteg('kjenteFeil');
      return;
    }
    setSteg('valg');
  }, [steg, spor, kort.length, mineDirekte, lukk]);

  /** «Send nytt innspill» (send a new submission) in the list: to the draft if there is one, otherwise a new form. */
  const nyttFraMine = useCallback(() => {
    setMineDirekte(false);
    if (harSkrevet) setSteg(stegForMine);
    else nullstill();
  }, [harSkrevet, stegForMine, nullstill]);

  const brukt = [...verdi].length;
  const forKort = brukt < innstillinger.minTextLength;
  const harUtkast = verdi.trim().length > 0;

  const fot = foten();

  return (
    <Dialog
      open={open}
      onClose={steg === 'kvittering' ? lukkOgNullstill : lukk}
      label={tekst.dialogSteg[steg]}
      kanLukkeVedScrim={!harUtkast}
      colorScheme={colorScheme}
      returnFocusTo={returnFocusTo}
      {...(fot ? { footer: fot } : {})}
    >
      {steg !== 'valg' && steg !== 'kvittering' ? <Btn variant="ghost" testId="tilbake" ikon="arrow-back-outline" label={tekst.tilbake} onClick={gåTilbake} /> : null}

      {offline && steg !== 'kvittering' ? (
        <Banner ikon="cloud-offline-outline" tone="info" testId="offline-banner">
          {tekst.offlineFor}
        </Banner>
      ) : null}

      {feil ? (
        <Banner live ikon="warning-outline" tone="danger" testId="sendefeil">
          {tekst.sendefeilBanner(feil)}
        </Banner>
      ) : null}

      {utlopt ? (
        <div className="innspel-stabel" data-gap="sm" data-testid="utlopt">
          <Banner live ikon="time-outline" tone="warning">
            {tekst.utloptTittel}
          </Banner>
          {onIdentityExpired ? (
            <Btn ikon="key-outline" testId="logg-inn-paa-nytt" label={tenantName ? tekst.loggInnPaaNytt(tenantName) : tekst.loggInnPaaNyttGenerisk} onClick={() => onIdentityExpired({ track: spor, text: verdi })} />
          ) : null}
          {onSendAnonymously ? (
            <Btn
              variant="secondary"
              testId="send-anonymt"
              label={tekst.sendAnonymt}
              hint={tekst.sendAnonymtHint}
              onClick={() => {
                setUtlopt(false);
                setSendNårAnonym(true);
                onSendAnonymously();
              }}
            />
          ) : (
            <Hint>{tekst.sendAnonymtHint}</Hint>
          )}
        </div>
      ) : null}

      {steg === 'valg' ? (
        <>
          <Valg onVelg={velgSpor} />
          {/* DD-62: med innspill fra før er dette en inngang på linje med sporene — flate og telling; uten står den dempet. Anonym: ingen liste, ingen knapp (DD-02). */}
          {!client.isAnonymous ? (
            <Btn
              variant={mine.length > 0 ? 'secondary' : 'ghost'}
              testId="mine-innspill"
              ikon="list-outline"
              label={mine.length > 0 ? tekst.mineMedAntall(mine.length) : tekst.mineTittel}
              ariaLabel={mine.length > 0 ? tekst.mineEtikett(mine.length) : undefined}
              onClick={() => void visMine()}
            />
          ) : null}
        </>
      ) : null}

      {steg === 'kjenteFeil' ? (
        <KjenteFeil
          kort={kort}
          spor={spor}
          laster={lasterKort}
          sender={sender}
          onJaOgsa={jaOgsa}
          onLigner={(k) => {
            setLenketTil(k);
            setSteg('felt');
          }}
        />
      ) : null}

      {steg === 'felt' ? (
        <Felt
          spor={spor}
          verdi={verdi}
          onEndre={endreTekst}
          minTextLength={innstillinger.minTextLength}
          textMaxLength={innstillinger.textMaxLength}
          kontekst={{ ...client.context, ...(skjerm ? { screen: skjerm } : {}) }}
          fjernedeFelter={fjernede}
          onFjernKontekst={(f) => setFjernede((x) => [...x, f])}
          {...(lenketTil ? { lenketTil, onFjernLenke: () => setLenketTil(undefined) } : {})}
          funn={funn}
          onSend={send}
          kanSende={!forKort && !sender}
          {...(skjermbildePå && screenshot ? { onTaBilde: taBilde } : {})}
          {...(skjermbildePå ? { onLastOppBilde: lastOpp } : {})}
          {...(bildeNotat ? { bildeNotat } : {})}
          harVedlegg={vedlegg !== undefined}
          vedleggFelt={vedleggFelt}
          onFjernVedlegg={fjernBilde}
        />
      ) : null}

      {steg === 'skjermbilde' && fangst ? (
        <Skjermbilde
          fangst={fangst}
          komponerer={komponerer}
          {...(bildeNotat ? { notat: bildeNotat } : {})}
          egne={egneMasker}
          onEgne={setEgneMasker}
          allTekst={allTekst}
          onAllTekst={setAllTekst}
          onFjern={fjernBilde}
        />
      ) : null}

      {steg === 'kvittering' && kvittering ? (
        <Kvittering
          art={kvittering.art}
          {...(kvittering.nr ? { saksnummer: kvittering.nr } : {})}
          {...(kvittering.vedlegg ? { vedlegg: kvittering.vedlegg } : {})}
          {...(kvittering.frist ? { frist: kvittering.frist } : {})}
          {...(kvittering.sak ? { sak: kvittering.sak } : {})}
          {...(tenantName ? { tenantName } : {})}
        />
      ) : null}

      {steg === 'mine' ? (
        <MySubmissions
          rader={mine}
          laster={lasterMine}
          storage={client.storage}
          {...(tenantName ? { tenantName } : {})}
          {...(mineSistKjentAt ? { sistKjentAt: mineSistKjentAt } : {})}
          leggTil={(id, text) => client.subjects.addAddendum(id, text)}
          onOppdatert={(sak) => {
            setMine((liste) => liste.map((r) => (r.pending !== true && r.id === sak.id ? sak : r)));
            oppdaterSistKjent(sistHentet, sak);
          }}
          minTextLength={innstillinger.minTextLength}
          textMaxLength={innstillinger.textMaxLength}
          onFeltApent={setTilleggApent}
        />
      ) : null}
    </Dialog>
  );

  /**
   * The footer carries ONE primary choice per step (C2), outside the scroll area. The choice
   * step has none — there the two surfaces ARE the action.
   */
  function foten() {
    if (steg === 'kjenteFeil') {
      return <Btn variant="secondary" testId="nei-noe-annet" label={tekst.svarNei} disabled={sender} onClick={() => setSteg('felt')} />;
    }
    if (steg === 'felt') {
      return (
        <Btn
          testId="send"
          label={sender ? tekst.sender : feil ? tekst.provIgjen : offline ? tekst.sendOffline : tekst.send}
          disabled={forKort || sender}
          busy={sender}
          hint={forKort ? tekst.forKort(innstillinger.minTextLength) : vedlegg ? tekst.sendHintMedBilde : form === 'modal' ? tekst.sendSnarvei : undefined}
          onClick={send}
        />
      );
    }
    if (steg === 'skjermbilde' && fangst) {
      return <Btn testId="legg-ved" label={tekst.leggVed} disabled={komponerer} onClick={() => void leggVed({ automasker: fangst.automasker, egne: egneMasker, allTekst })} />;
    }
    if (steg === 'kvittering') {
      return <Btn testId="kvittering-lukk" label={tekst.lukk} onClick={lukkOgNullstill} autoFocus />;
    }
    if (steg === 'mine') {
      // F32: én primærhandling om gangen — feltet i kortet eier foten mens det er åpent.
      if (tilleggApent) return null;
      // H3.1, skjerm 3: fra en gammel liste (uten nett) er knappen omriss — det som skrives, sendes når nettet er tilbake (DD-41).
      return (
        <Btn
          testId="send-nytt"
          ikon="chatbubble-outline"
          label={mine.length === 0 ? tekst.sendForste : tekst.sendNytt}
          onClick={nyttFraMine}
          variant={mineSistKjentAt === undefined ? 'primary' : 'secondary'}
        />
      );
    }
    return null;
  }
}

/**
 * The call's context: `screen` for this opening, and the lines the submitter has
 * removed set to `undefined` (DD-05). That is how the core (0.2.1) tells
 * «remove» from «keep from init» — leaving the key out would fill the field again.
 * The fields are a closed allow-list, so what is not in `client.context`
 * does not exist to be removed.
 */
function fjernetKontekst(client: InnspelClient, fjernede: string[], skjerm: string | undefined): Record<string, unknown> {
  const ut: Record<string, unknown> = {};
  if (skjerm) ut['screen'] = skjerm;
  for (const k of [...Object.keys(client.context), 'screen']) {
    if (k !== 'sdkVersion' && fjernede.includes(k)) ut[k] = undefined;
  }
  return ut;
}
